import { Monitor, MonitorCheck, MonitorOff, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/ui/button";
import { Separator } from "@/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/tooltip";
import { cn } from "@/utils/tailwind";

import type { Theme, ThemeMode } from "./theme";

interface ThemeControlButtonsProps {
  className?: string;
  compact?: boolean;
  currentTheme: Theme;
  isSystem: boolean;
  onThemeChange?: (theme: ThemeMode) => void;
}

// Share the complete theme-control interaction and animation while allowing
// each surface to choose how its preference is persisted.
export function ThemeControlButtons({
  className,
  compact = false,
  currentTheme,
  isSystem,
  onThemeChange,
}: ThemeControlButtonsProps) {
  const [showIntermediateMonitorIcon, setShowIntermediateMonitorIcon] =
    useState(false);
  const isDark = currentTheme === "dark";
  const buttonType = onThemeChange ? "button" : "submit";

  useEffect(() => {
    if (showIntermediateMonitorIcon) {
      const timer = setTimeout(() => {
        setShowIntermediateMonitorIcon(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [showIntermediateMonitorIcon]);

  // Show the same brief monitor transition when leaving system mode, then let
  // the owning surface persist the explicit light or dark choice.
  function selectColorTheme() {
    if (isSystem) setShowIntermediateMonitorIcon(true);
    onThemeChange?.(isDark ? "light" : "dark");
  }

  // Show the monitor transition even when system mode is already selected so
  // repeated interaction receives immediate visual feedback.
  function selectSystemTheme() {
    setShowIntermediateMonitorIcon(true);
    onThemeChange?.("system");
  }

  return (
    <div className={cn("flex items-center", className)}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={`Switch to ${isDark ? "light" : "dark"} theme`}
              name="theme"
              size="icon"
              type={buttonType}
              value={isDark ? "light" : "dark"}
              variant="ghost"
              className={cn(
                isSystem
                  ? "text-muted-foreground hover:text-foreground"
                  : "text-foreground",
                compact && "size-7",
              )}
              onClick={selectColorTheme}
            >
              <span className={cn("grid", compact ? "size-3.5" : "size-4")}>
                <Sun
                  className={cn(
                    "transition-all duration-300 [grid-area:1/1] motion-reduce:transition-none",
                    compact ? "size-3.5" : "size-4",
                    isDark
                      ? "scale-0 rotate-90 opacity-0"
                      : "scale-100 rotate-0 opacity-100",
                  )}
                />
                <Moon
                  className={cn(
                    "transition-all duration-300 [grid-area:1/1] motion-reduce:transition-none",
                    compact ? "size-3.5" : "size-4",
                    isDark
                      ? "scale-100 rotate-0 opacity-100"
                      : "scale-0 -rotate-90 opacity-0",
                  )}
                />
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={5}>
            Toggle theme
          </TooltipContent>
        </Tooltip>

        <Separator
          className="bg-muted-foreground data-[orientation=vertical]:h-3.5"
          orientation="vertical"
        />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label="Use system theme"
              aria-pressed={isSystem}
              name="theme"
              size="icon"
              type={buttonType}
              value="system"
              variant="ghost"
              className={cn(
                isSystem
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
                compact && "size-7",
              )}
              onClick={selectSystemTheme}
            >
              {isSystem ? (
                showIntermediateMonitorIcon ? (
                  <Monitor className={compact ? "size-3.5" : "size-4"} />
                ) : (
                  <MonitorCheck className={compact ? "size-3.5" : "size-4"} />
                )
              ) : showIntermediateMonitorIcon ? (
                <Monitor className={compact ? "size-3.5" : "size-4"} />
              ) : (
                <MonitorOff className={compact ? "size-3.5" : "size-4"} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={5}>
            Use system theme
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
